// localStorage/sessionStorage wrapper that never throws (blocked storage, private mode, quota).
// Reads return null on any failure; writes return false. Keys written by new code use the "dsk:" prefix.

type Area = 'local' | 'session';

function area(kind: Area): Storage | null {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function isQuotaError(err: unknown): boolean {
  return (
    typeof DOMException !== 'undefined' &&
    err instanceof DOMException &&
    (err.name === 'QuotaExceededError' || err.name === 'NS_ERROR_DOM_QUOTA_REACHED' || err.code === 22)
  );
}

export interface SetOptions {
  kind?: Area;
  /** Called once when the quota is hit; drop the oldest history entries here. The write is then retried once. */
  evict?: () => void;
}

export const safeStorage = {
  get(key: string, kind: Area = 'local'): string | null {
    try {
      return area(kind)?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },

  set(key: string, value: string, opts: SetOptions = {}): boolean {
    const store = area(opts.kind ?? 'local');
    if (!store) return false;
    try {
      store.setItem(key, value);
      return true;
    } catch (err) {
      if (isQuotaError(err) && opts.evict) {
        try {
          opts.evict();
          store.setItem(key, value);
          return true;
        } catch {
          return false;
        }
      }
      return false;
    }
  },

  remove(key: string, kind: Area = 'local'): boolean {
    try {
      const store = area(kind);
      if (!store) return false;
      store.removeItem(key);
      return true;
    } catch {
      return false;
    }
  },

  /** True when the storage area can actually be written to. */
  available(kind: Area = 'local'): boolean {
    const store = area(kind);
    if (!store) return false;
    try {
      const probe = 'dsk:probe';
      store.setItem(probe, '1');
      store.removeItem(probe);
      return true;
    } catch {
      return false;
    }
  },

  getJson<T>(key: string, fallback: T, kind: Area = 'local'): T {
    const raw = safeStorage.get(key, kind);
    if (raw == null) return fallback;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return fallback;
    }
  },

  setJson(key: string, value: unknown, opts: SetOptions = {}): boolean {
    try {
      return safeStorage.set(key, JSON.stringify(value), opts);
    } catch {
      return false;
    }
  },
};
